<script setup lang="ts">
/* eslint-disable max-lines -- Markup, handlers and styles for every filter group in one SFC. */
import { computed, ref, watch } from 'vue';

import Autocomplete from '@/components/Shared/Input/Autocomplete/Autocomplete.vue';
import FilterChip from './FilterChip.vue';
import IncludeUnknownToggle from './IncludeUnknownToggle.vue';
import InsightsFilterHeading from './InsightsFilterHeading.vue';
import {
  DISTANCE_OPTIONS,
  LANGUAGE_OPTIONS,
  LAST_ACTIVE_OPTIONS,
  RECENTLY_OPEN_LABEL,
  RESPONSIVENESS_OPTIONS,
  SHIFT_OPTIONS,
} from './filter-options';
import TextField from '@/components/Shared/Input/TextInputs/TextField.vue';
import SparkleIcon from '@/assets/svg/sparkle.svg?component';
import { SearchService as SharedSearchService } from '@/core/shared/search/search.service';
import { SelectedFilterType } from '@/core/sourcing/search/types/selected-filter-type.type';
import { DEFAULT_AROUND_RADIUS } from '@/core/sourcing/search/consts/default-seach-query.const';
import {
  type FilterableLanguage,
  type FilterableResponsiveness,
  type FilterableShift,
  MAX_HOURLY_PAY_RATE,
} from '@/core/sourcing/search/consts/hard-filters.const';
import { useAddressPredictions } from '@/composables/useAddressPredictions';

// The smart-input header owns query execution: the sidebar only stages pending filter
// state, picked up by the next explicit search (header Search button / Enter). Changing
// a filter never runs a search on its own.

// When embedded in the view's collapsible panel, the view renders the Filters
// title/count/Clear-all, so the card's own header is suppressed to avoid duplication.
withDefaults(defineProps<{ hideHeader?: boolean }>(), { hideHeader: false });

// Apply runs the search the staged filters describe. The view answers this by calling the
// header's own submit rather than rebuilding its payload, so both buttons are one action with
// two placements: `effectiveMode` there accounts for the forced-mode chip and job mode, and a
// second construction of it would drift.
const emit = defineEmits<{ apply: [] }>();

function onApply(): void {
  emit('apply');
}

// Same store field the header's `is-searching` prop is built from, read directly rather than
// threaded through as a prop so the two buttons cannot disagree about whether a search is in
// flight. The header's own submit already refuses to fire while searching, so this is about
// telling the recruiter the click landed: without it Apply looks inert, since the results it
// affects are on the other side of the screen.
const isSearching = computed(() => sharedSearchService.isSearchingCandidates);

const sharedSearchService = new SharedSearchService();

const hasLocationFilter = computed(() => !!sharedSearchService.location);

// Location and Distance share one group and one "From search" marker, hung on the group
// label. Distance carries no label of its own, so a marker of its own would float above the
// field with nothing to attach to, and would push the field away from Location.
const locationGroupInferred = computed(() => {
  const location = sharedSearchService.locationInferred && !!sharedSearchService.location;
  const distance = sharedSearchService.distanceInferred && !!sharedSearchService.distance;
  return { location, distance, any: location || distance };
});

// One marker covers both fields, so its title names which of them the search supplied.
const locationGroupInferredTitle = computed(() => {
  const { location, distance } = locationGroupInferred.value;
  if (location && distance) {
    return 'Location and distance from search';
  }
  return distance ? 'Distance from search' : 'Location from search';
});
// Pay is one group over two bounds, so one marker covers either being inferred; each bound
// keeps its own owner and mode in the service.
const payInferred = computed(
  () =>
    sharedSearchService.isDossierFilterInferred(SelectedFilterType.PAY_RATE_MIN) ||
    sharedSearchService.isDossierFilterInferred(SelectedFilterType.PAY_RATE_MAX),
);

// Mobility and Engagement group several filters under one label, so, like Location, one marker
// per group names which of its chips the search supplied.
function inferredTitle(parts: [string, SelectedFilterType][]): string | undefined {
  const names = parts
    .filter(([, type]) => sharedSearchService.isDossierFilterInferred(type))
    .map(([name]) => name);
  return names.length ? `${names.join(' and ')} from search` : undefined;
}
const mobilityInferredTitle = computed(() =>
  inferredTitle([
    ['Open to travel', SelectedFilterType.OPEN_TO_TRAVEL],
    ['Open to relocate', SelectedFilterType.WILLING_TO_RELOCATE],
  ]),
);
const engagementInferredTitle = computed(() =>
  inferredTitle([
    [RECENTLY_OPEN_LABEL, SelectedFilterType.LAST_OPEN_TO_OPPORTUNITIES],
    [RESPONSIVENESS_OPTIONS[0].label, SelectedFilterType.RESPONSIVENESS],
  ]),
);

const distancePlaceholder = computed(() =>
  hasLocationFilter.value ? 'Any distance' : 'Select a location first',
);

// The service owns the count (mirrored by the panel toggle): Distance is a radius
// modifier of Location and isn't counted.
const activeCount = computed(() => sharedSearchService.activeSidebarFilterCount);

// Pay inputs are free text locally; only values the server would honour are staged —
// a bad bound would be silently dropped server-side, looking applied while doing nothing.
const payRateMinInput = ref(sharedSearchService.payRateMin?.toString() ?? '');
const payRateMaxInput = ref(sharedSearchService.payRateMax?.toString() ?? '');

function parsePayInput(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }

  const amount = Number(trimmed);
  if (!Number.isFinite(amount) || amount <= 0 || amount > MAX_HOURLY_PAY_RATE) {
    return undefined;
  }

  return amount;
}

const payError = computed(() => {
  const minRaw = payRateMinInput.value.trim();
  const maxRaw = payRateMaxInput.value.trim();
  const min = parsePayInput(minRaw);
  const max = parsePayInput(maxRaw);

  if ((minRaw && min === undefined) || (maxRaw && max === undefined)) {
    return `Enter an hourly rate between $1 and $${MAX_HOURLY_PAY_RATE}`;
  }
  if (min !== undefined && max !== undefined && min > max) {
    return 'Min can’t exceed max';
  }

  return '';
});

// Guards the sync watcher below against the store writes the inputs themselves make:
// local text must survive its own staging (a contradictory range stages `undefined`
// while the recruiter is mid-correction), while every external write syncs verbatim.
// Shape-based guesses at a write's origin can't tell hydration from local staging.
let stagingPayInputs = false;

function stagePayFilters(
  edited: SelectedFilterType.PAY_RATE_MIN | SelectedFilterType.PAY_RATE_MAX,
): void {
  const min = parsePayInput(payRateMinInput.value);
  const max = parsePayInput(payRateMaxInput.value);
  // A contradictory range (min > max) matches nothing the recruiter intends; stage
  // neither bound until the inputs make sense together.
  const contradictory = min !== undefined && max !== undefined && min > max;

  stagingPayInputs = true;
  try {
    if (contradictory) {
      sharedSearchService.unstagePayRange(edited);
    } else {
      sharedSearchService.stagePayRange(min, max, edited);
    }
  } finally {
    stagingPayInputs = false;
  }
}

// The other controls bind straight to the store, but pay keeps local text (to hold
// invalid or mid-correction input), so store values written from outside these inputs —
// URL hydration lands after this component mounts — must be pushed into the text, sets
// and unsets alike, or editing one bound would re-stage a value hydration removed.
// flush: 'sync' makes the callback run inside the write, while `stagingPayInputs`
// still marks writes the inputs themselves caused.
watch(
  () => [sharedSearchService.payRateMin, sharedSearchService.payRateMax] as const,
  ([min, max]) => {
    if (stagingPayInputs) {
      return;
    }

    payRateMinInput.value = min !== undefined ? `${min}` : '';
    payRateMaxInput.value = max !== undefined ? `${max}` : '';
  },
  { flush: 'sync' },
);

function onPayMinInput(value: string): void {
  payRateMinInput.value = value ?? '';
  stagePayFilters(SelectedFilterType.PAY_RATE_MIN);
}

function onPayMaxInput(value: string): void {
  payRateMaxInput.value = value ?? '';
  stagePayFilters(SelectedFilterType.PAY_RATE_MAX);
}

function toggleShift(shift: FilterableShift): void {
  const shifts = sharedSearchService.shifts;
  sharedSearchService.shifts = shifts.includes(shift)
    ? shifts.filter((s) => s !== shift)
    : [...shifts, shift];
}

function toggleLanguage(language: FilterableLanguage): void {
  const languages = sharedSearchService.languages;
  sharedSearchService.languages = languages.includes(language)
    ? languages.filter((l) => l !== language)
    : [...languages, language];
}

function toggleResponsiveness(level: FilterableResponsiveness): void {
  const levels = sharedSearchService.responsiveness;
  sharedSearchService.responsiveness = levels.includes(level)
    ? levels.filter((l) => l !== level)
    : [...levels, level];
}

// syncFiltersToSourcingSearch resolves this to a RECENT_OPEN_TO_WORK_DAYS window, so the sidebar
// stages on/off rather than a day count.
function toggleRecentlyOpen(): void {
  sharedSearchService.recentlyOpenToWork = !sharedSearchService.recentlyOpenToWork;
}

// Mobility reads as a chip row like the others, but each chip is its own presence-modeled
// filter (WILLING_TO_RELOCATE / OPEN_TO_TRAVEL), so selecting both ANDs them and narrows the
// result. Shift and language ship as a single filter with a list value, meaning "any of
// these", where selecting more chips widens it. Same affordance, opposite direction.
function toggleRelocate(): void {
  sharedSearchService.willingToRelocate = !sharedSearchService.willingToRelocate;
}

function toggleTravel(): void {
  sharedSearchService.openToTravel = !sharedSearchService.openToTravel;
}

const {
  isLoading: isLoadingLocations,
  listItems: listOfLocations,
  reset: resetLocationPredictions,
  search: onSearchLocation,
  searchText: searchLocationText,
} = useAddressPredictions((address) => sharedSearchService.getAddressPredictions(address));

const isLocationFocused = ref(false);
function onLocationFocused(value: boolean) {
  isLocationFocused.value = value;
}
const locationPlaceholder = computed(() =>
  isLocationFocused.value ? 'Type to search' : 'City, state',
);
const noDataText = computed(() =>
  searchLocationText.value.trim() === '' ? 'Type to search' : 'No results',
);

function onLocationChange(value?: string | number) {
  // Removing an inferred value must also suppress re-inferring it, or the re-run of the
  // same query brings it right back and the X is a no-op (ONE-6178).
  const locationWasInferred = sharedSearchService.locationInferred;
  const distanceWasInferred = sharedSearchService.distanceInferred;

  sharedSearchService.location = (value as string) || undefined;
  // A user-chosen location owns the value, so drop the inferred sparkle and the job's
  // coordinates — the geo fence now comes from this string (geocoded server-side), not the job.
  sharedSearchService.locationInferred = false;
  sharedSearchService.activeJobCoordinates = undefined;

  // Distance is only meaningful with a location: default it to 30mi on select,
  // drop it when the location is cleared. Also clear the inferred flag so a later
  // location selection (which defaults distance to 30) doesn't render a false sparkle.
  if (!sharedSearchService.location) {
    if (locationWasInferred) {
      sharedSearchService.suppressInferredType(SelectedFilterType.LOCATION);
    }
    if (distanceWasInferred) {
      sharedSearchService.suppressInferredType(SelectedFilterType.AROUND_RADIUS);
    }
    sharedSearchService.distance = undefined;
    sharedSearchService.distanceInferred = false;
    resetLocationPredictions();
  } else {
    // A user-chosen location owns the radius: drop any job/NL-inferred distance sparkle so it
    // isn't misattributed to AI, and seed the default when no distance is set yet (a location
    // always carries a radius server-side).
    sharedSearchService.distanceInferred = false;
    if (!sharedSearchService.distance) {
      sharedSearchService.distance = DEFAULT_AROUND_RADIUS;
    }
  }
}

function onDistanceChange(value?: string | number) {
  const distanceWasInferred = sharedSearchService.distanceInferred;

  sharedSearchService.distance = (value as number) || undefined;
  // A user-chosen distance owns the value, so drop the inferred sparkle.
  sharedSearchService.distanceInferred = false;

  if (!sharedSearchService.distance) {
    // A location-anchored search always carries a radius server-side and can't truly be
    // "Any distance": a user-owned location, or job mode (the search fences on the job's
    // location, which the recruiter can't remove here). Snap those clears back to the default
    // instead of showing a misleading "Any distance" while a 30mi fence still applies
    // (ONE-6457). Only an AI-inferred radius whose location anchor IS removable (NL) is
    // suppressed, so the recruiter can actually drop it.
    const anchoredByJob = !!sharedSearchService.activeJob;
    const anchoredByUserLocation =
      !!sharedSearchService.location && !sharedSearchService.locationInferred;
    if (anchoredByJob || anchoredByUserLocation) {
      sharedSearchService.distance = DEFAULT_AROUND_RADIUS;
    } else if (distanceWasInferred) {
      sharedSearchService.suppressInferredType(SelectedFilterType.AROUND_RADIUS);
    }
  }
}

function onLastActiveChange(value?: string | number) {
  const lastActiveWasInferred = sharedSearchService.lastActiveInferred;

  sharedSearchService.lastActive = (value as number) || undefined;
  // A user-chosen recency owns the value, so drop the inferred sparkle.
  sharedSearchService.lastActiveInferred = false;

  if (!sharedSearchService.lastActive && lastActiveWasInferred) {
    sharedSearchService.suppressInferredType(SelectedFilterType.LAST_ACTIVE);
  }
}

function clearAll() {
  // Clearing wipes inferred values too — suppress them so the re-run doesn't immediately
  // re-infer what the recruiter just removed.
  if (sharedSearchService.locationInferred) {
    sharedSearchService.suppressInferredType(SelectedFilterType.LOCATION);
  }
  if (sharedSearchService.distanceInferred) {
    sharedSearchService.suppressInferredType(SelectedFilterType.AROUND_RADIUS);
  }
  if (sharedSearchService.lastActiveInferred) {
    sharedSearchService.suppressInferredType(SelectedFilterType.LAST_ACTIVE);
  }

  sharedSearchService.location = undefined;
  sharedSearchService.distance = undefined;
  sharedSearchService.lastActive = undefined;
  // Clearing the values also drops their inferred sparkles and the job's coordinates, so
  // the next search doesn't silently fence on a stale job location.
  sharedSearchService.locationInferred = false;
  sharedSearchService.distanceInferred = false;
  sharedSearchService.lastActiveInferred = false;
  sharedSearchService.activeJobCoordinates = undefined;
  // Also suppresses the inferred insight filters it clears.
  sharedSearchService.clearHardFilters();
  payRateMinInput.value = '';
  payRateMaxInput.value = '';
  resetLocationPredictions();
}

// Lets the view's collapsible panel trigger a reset from its own Clear-all control.
defineExpose({ clearAll });
</script>

<template>
  <aside class="smart-filters">
    <div v-if="!hideHeader" class="filters-head">
      <!-- No active count beside the title: on desktop every chip and field is already
           visible. `activeCount` is still what gates Clear-all below, and the mobile panel
           shows it on its collapsed toggle, where it is the only applied-filters signal. -->
      <div class="filters-title">Filters</div>
      <button
        class="filters-clear"
        :disabled="activeCount === 0"
        data-test="smart-filters-clear"
        @click="clearAll"
      >
        Clear all
      </button>
    </div>

    <!-- Distance is a radius modifier of Location rather than a filter of its own, so the two
         share one labelled group and the radius reads off the select's own value. -->
    <div class="filter-group">
      <div class="filter-label-row">
        <label>Location</label>
        <!-- One sparkle for the group: marks a location or radius inferred from the search
             (NL mode) or the active job. -->
        <span
          v-if="locationGroupInferred.any"
          class="inferred-sparkle"
          :title="locationGroupInferredTitle"
          data-test="smart-filters-location-group-inferred"
          :data-location-inferred="locationGroupInferred.location"
          :data-distance-inferred="locationGroupInferred.distance"
        >
          <SparkleIcon viewBox="0 0 16 20" />
        </span>
      </div>
      <Autocomplete
        :model-value="sharedSearchService.location"
        :placeholder="locationPlaceholder"
        :items="listOfLocations"
        :loading="isLoadingLocations"
        :no-data-text="noDataText"
        open-text="Type to search"
        no-filter
        clearable
        plain
        data-test="smart-filters-location"
        @update:search="onSearchLocation"
        @update:focused="onLocationFocused"
        @update:model-value="onLocationChange"
      />
      <!-- Sits directly beneath the location field, per the design; its inferred marker rides
           on the group label above. -->
      <Autocomplete
        :model-value="sharedSearchService.distance"
        :placeholder="distancePlaceholder"
        :items="DISTANCE_OPTIONS"
        item-title="title"
        item-value="id"
        :disabled="!hasLocationFilter"
        clearable
        plain
        class="distance-select"
        data-test="smart-filters-distance"
        @update:model-value="onDistanceChange"
      />
    </div>

    <div class="filter-group">
      <div class="filter-label-row">
        <label>Last active</label>
        <!-- Sparkle marks a recency inferred from the search (NL mode). -->
        <span
          v-if="sharedSearchService.lastActiveInferred && sharedSearchService.lastActive"
          class="inferred-sparkle"
          title="From search"
          data-test="smart-filters-last-active-inferred"
        >
          <SparkleIcon viewBox="0 0 16 20" />
        </span>
      </div>
      <Autocomplete
        :model-value="sharedSearchService.lastActive"
        placeholder="Any time"
        :items="LAST_ACTIVE_OPTIONS"
        item-title="title"
        item-value="id"
        clearable
        plain
        data-test="smart-filters-last-active"
        @update:model-value="onLastActiveChange"
      />
    </div>

    <InsightsFilterHeading />

    <div class="filter-group">
      <div class="filter-label-row">
        <label class="insight-label">Target pay ($/hr)</label>
        <span
          v-if="payInferred"
          class="inferred-sparkle"
          title="From search"
          data-test="smart-filters-pay-inferred"
        >
          <SparkleIcon viewBox="0 0 16 20" />
        </span>
      </div>
      <!-- hide-details on both: v-text-field otherwise reserves a 22px message row per field
           that nothing fills, since pay validation renders through .pay-error below. Without
           it each input measures 62px against the design's 40 and the slack reads as a gap
           before the next section. -->
      <div class="pay-inputs">
        <TextField
          :model-value="payRateMinInput"
          placeholder="Min"
          inputmode="decimal"
          :error="!!payError"
          hide-details
          data-test="smart-filters-pay-min"
          @update:model-value="onPayMinInput"
        />
        <span class="pay-separator">–</span>
        <TextField
          :model-value="payRateMaxInput"
          placeholder="Max"
          inputmode="decimal"
          :error="!!payError"
          hide-details
          data-test="smart-filters-pay-max"
          @update:model-value="onPayMaxInput"
        />
      </div>
      <p v-if="payError" class="pay-error" data-test="smart-filters-pay-error">
        {{ payError }}
      </p>
      <IncludeUnknownToggle :types="[SelectedFilterType.PAY_RATE_MAX]" />
    </div>

    <div class="filter-group">
      <div class="filter-label-row">
        <label id="smart-filters-shift-label" class="insight-label">Shift preference</label>
        <span
          v-if="sharedSearchService.isDossierFilterInferred(SelectedFilterType.SHIFT)"
          class="inferred-sparkle"
          title="From search"
          data-test="smart-filters-shift-inferred"
        >
          <SparkleIcon viewBox="0 0 16 20" />
        </span>
      </div>
      <div class="chip-row" role="group" aria-labelledby="smart-filters-shift-label">
        <FilterChip
          v-for="option in SHIFT_OPTIONS"
          :key="option.value"
          :label="option.label"
          :selected="sharedSearchService.shifts.includes(option.value)"
          category="preference"
          :data-test="`smart-filters-shift-${option.value}`"
          @toggle="toggleShift(option.value)"
        />
      </div>
      <IncludeUnknownToggle :types="[SelectedFilterType.SHIFT]" />
    </div>

    <div class="filter-group">
      <div class="filter-label-row">
        <label id="smart-filters-mobility-label" class="insight-label">Mobility</label>
        <span
          v-if="mobilityInferredTitle"
          class="inferred-sparkle"
          :title="mobilityInferredTitle"
          data-test="smart-filters-mobility-inferred"
        >
          <SparkleIcon viewBox="0 0 16 20" />
        </span>
      </div>
      <div class="chip-row" role="group" aria-labelledby="smart-filters-mobility-label">
        <FilterChip
          label="Open to travel"
          :selected="sharedSearchService.openToTravel"
          category="preference"
          data-test="smart-filters-travel"
          @toggle="toggleTravel"
        />
        <FilterChip
          label="Open to relocate"
          :selected="sharedSearchService.willingToRelocate"
          category="preference"
          data-test="smart-filters-relocate"
          @toggle="toggleRelocate"
        />
      </div>
      <IncludeUnknownToggle
        :types="[SelectedFilterType.OPEN_TO_TRAVEL, SelectedFilterType.WILLING_TO_RELOCATE]"
      />
    </div>

    <div class="filter-group">
      <div class="filter-label-row">
        <label id="smart-filters-language-label" class="insight-label">Language</label>
        <span
          v-if="sharedSearchService.isDossierFilterInferred(SelectedFilterType.LANGUAGE)"
          class="inferred-sparkle"
          title="From search"
          data-test="smart-filters-language-inferred"
        >
          <SparkleIcon viewBox="0 0 16 20" />
        </span>
      </div>
      <div class="chip-row" role="group" aria-labelledby="smart-filters-language-label">
        <FilterChip
          v-for="option in LANGUAGE_OPTIONS"
          :key="option.value"
          :label="option.label"
          :selected="sharedSearchService.languages.includes(option.value)"
          category="language"
          :data-test="`smart-filters-language-${option.value}`"
          @toggle="toggleLanguage(option.value)"
        />
      </div>
      <IncludeUnknownToggle :types="[SelectedFilterType.LANGUAGE]" />
    </div>

    <!-- The card gives these two signals the intent family, so matching it here keeps a filter
         and the chip it corresponds to reading as one system. -->
    <div class="filter-group">
      <div class="filter-label-row">
        <label id="smart-filters-engagement-label" class="insight-label">Engagement</label>
        <span
          v-if="engagementInferredTitle"
          class="inferred-sparkle"
          :title="engagementInferredTitle"
          data-test="smart-filters-engagement-inferred"
        >
          <SparkleIcon viewBox="0 0 16 20" />
        </span>
      </div>
      <div class="chip-row" role="group" aria-labelledby="smart-filters-engagement-label">
        <FilterChip
          :label="RECENTLY_OPEN_LABEL"
          :selected="sharedSearchService.recentlyOpenToWork"
          category="intent"
          data-test="smart-filters-recently-open"
          @toggle="toggleRecentlyOpen"
        />
        <FilterChip
          v-for="option in RESPONSIVENESS_OPTIONS"
          :key="option.value"
          :label="option.label"
          :selected="sharedSearchService.responsiveness.includes(option.value)"
          category="intent"
          :data-test="`smart-filters-responsiveness-${option.value}`"
          @toggle="toggleResponsiveness(option.value)"
        />
      </div>
      <IncludeUnknownToggle
        :types="[SelectedFilterType.LAST_OPEN_TO_OPPORTUNITIES, SelectedFilterType.RESPONSIVENESS]"
      />
    </div>

    <!-- Sidebar changes only stage, so without a control down here the recruiter has to cross
         the screen to the header's Search to see them take effect, with nothing saying so. -->
    <button
      type="button"
      class="filters-apply"
      :disabled="isSearching"
      data-test="smart-filters-apply"
      @click="onApply"
    >
      {{ isSearching ? 'Applying…' : 'Apply' }}
    </button>
  </aside>
</template>

<style scoped lang="postcss">
.smart-filters {
  @apply rounded-xl border border-tint-80 bg-tint-0 p-4 shadow-card;
}

.filters-head {
  @apply mb-3.5 flex items-center justify-between;
}

.filters-title {
  @apply text-[13.5px] font-bold tracking-[0.01em] text-shade-880;
}

.filters-clear {
  @apply rounded-md px-1.5 py-1 text-[11.5px] text-shade-800 transition-colors;

  &:hover:not(:disabled) {
    @apply bg-tint-40 text-shade-880;
  }

  &:disabled {
    @apply cursor-default text-tint-400;
  }
}

.filter-group {
  @apply mb-4;

  &:last-child {
    @apply mb-0;
  }
}

.filter-label-row {
  @apply mb-1.5 flex items-center gap-1.5;

  label {
    @apply text-[11px] font-bold uppercase tracking-[0.06em] text-shade-700;
  }
}

/* Insight filters sit under their own heading, so their labels are sentence-case and darker:
   they read as fields within that block rather than as top-level sections like LOCATION. */
.filter-label-row label.insight-label {
  @apply text-xs normal-case tracking-normal text-shade-880;
}

.distance-select {
  @apply mt-2;
}

.chip-row {
  @apply flex flex-wrap items-center gap-1;
}

/* The header's .btn-search in light-surface form: same primary fill and metrics, full width
   because it anchors the panel rather than sitting inline beside an input. */
.filters-apply {
  @apply mt-5 w-full rounded-[10px] bg-highlight-500 px-5 py-2.5 transition-colors;
  @apply text-[13.5px] font-bold tracking-[0.01em] text-white;

  &:hover:not(:disabled) {
    @apply bg-highlight-600;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  /* The header's .btn-search:disabled is a dark fill for the dark header; on the light panel
     the equivalent is the panel's own border grey with muted text. */
  &:disabled {
    @apply cursor-not-allowed bg-tint-80 text-tint-400;
  }
}

.pay-inputs {
  @apply flex items-center gap-2;
}

.pay-separator {
  @apply text-shade-700;
}

.pay-error {
  @apply mt-1 text-[11.5px] text-critical-500;
}

.inferred-sparkle {
  @apply inline-flex h-4 w-4 items-center justify-center rounded-full text-highlight-500;
  background: rgba(108, 64, 229, 0.12);
  border: 1px solid rgba(108, 64, 229, 0.22);

  svg {
    @apply h-2.5 w-2.5;
  }
}
</style>
